import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, Trash2Icon, Loader2Icon, LayoutTemplateIcon, TagIcon, CalendarIcon, ChevronRightIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formTemplatesLibraryApi, FormTemplateRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function FormTemplatesLibrary() {
  const navigate = useNavigate();
  const {
    currentWorkspace
  } = useWorkspace();
  const [templates, setTemplates] = useState<FormTemplateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    loadTemplates();
  }, [currentWorkspace.name]);
  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const all = await formTemplatesLibraryApi.list();
      const filtered = all.filter(t => !t.Team || t.Team === currentWorkspace.name);
      setTemplates(filtered);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to load form templates', {
        description: msg
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await formTemplatesLibraryApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      toast.success('Form template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };
  const filtered = templates.filter(t => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4560" data-mp="1j147d8u8cqeh">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4561" data-mp="1e201ar2lr8sd" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4562" data-mp="1e0os2bqe18tv">
          Loading form templates...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4563" data-mp="lencsbcvsqwf">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4564" data-mp="lff4vc79s6hy">
        <div data-id="element-4565" data-mp="104bcx99btjik">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4566" data-mp="10430tjl12i8f">
            Form Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4567" data-mp="v9yt2y3xu66a">
            Reusable form templates you can use when creating new signup forms.
          </p>
        </div>
        <button onClick={() => navigate('/form-templates/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4568" data-mp="2mbd5wdm43pz">
          <PlusIcon className="h-4 w-4" data-id="element-4569" data-mp="2j397bobj075s" />
          New Template
        </button>
      </div>

      {templates.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-4570" data-mp="29b3ket04sfxg">
          <div className="relative flex-1 min-w-0" data-id="element-4571" data-mp="2x0pkdiobuz6i">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4572" data-mp="2wz4n3lamp24y" />
            <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4573" data-mp="31y7sx7yfncdz" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-4574" data-mp="1gn7ohqidys1q">
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4575" data-mp="1s2cbtgk203nc">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-4576" data-mp="1n3j0nus8ooqc">
            <LayoutTemplateIcon className="h-8 w-8 text-gray-400" data-id="element-4577" data-mp="1i68zxizegqn7" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-4578" data-mp="2fthppq6folv0">
            {searchQuery ? 'No matching templates' : 'No form templates yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-4579" data-mp="2arsdi77h625c">
            {searchQuery ? 'Try adjusting your search.' : 'Create reusable form templates to speed up building new signup forms.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/form-templates/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4580" data-mp="uh6lsl3z5fxg">
              <PlusIcon className="h-4 w-4" data-id="element-4581" data-mp="362ypd0xjjgiu" />
              Create First Template
            </button>}
        </div> : <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4582" data-mp="3b10iyfsley8k">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4583" data-mp="3ftbfr9s1e3ww">
            <thead className="bg-gray-50" data-id="element-4584" data-mp="3fumpdq85y5vx">
              <tr data-id="element-4585" data-mp="3kqtj8knabqxi">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4586" data-mp="3krd161cxqfbj">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4587" data-mp="3ts1hx3tpqts4">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4588" data-mp="2k5p1ptrwo6m">
                  Mode
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4589" data-mp="24eo8j06qufk5">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-4590" data-mp="32qhc6d5a6rro">
              {filtered.map(tpl => <tr key={tpl.ID} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/form-templates/${tpl.ID}`)} data-id="element-4591" data-mp="3gq9yzxdosnud">
                  <td className="px-6 py-4" data-id="element-4592" data-mp="3qmd0r24mmkgs">
                    <div className="flex items-center gap-3" data-id="element-4593" data-mp="3ql1vexvj94nk">
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-4594" data-mp="3lnijw5q7z5zb">
                        <LayoutTemplateIcon className="h-4 w-4 text-indigo-600" data-id="element-4595" data-mp="39izj3qiuear" />
                      </div>
                      <div className="min-w-0" data-id="element-4596" data-mp="3vg7qonatrvob">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-4597" data-mp="d7p4xvbje2kd">
                          {tpl.Template_Name || 'Untitled'}
                        </p>
                        {tpl.Description && <p className="text-xs text-gray-500 truncate max-w-xs" data-id="element-4598" data-mp="rwicmraq00wu">
                            {tpl.Description}
                          </p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4599" data-mp="mvue7kk5whsl">
                    {tpl.Category ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-4600" data-mp="94pjmwvnifp4">
                        <TagIcon className="h-3 w-3" data-id="element-4601" data-mp="dwhal8ipynfs" />
                        {tpl.Category}
                      </span> : <span className="text-xs text-gray-400" data-id="element-4602" data-mp="3va2z06gg04a7">—</span>}
                  </td>
                  <td className="px-6 py-4" data-id="element-4603" data-mp="3mbhjkk69p5dn">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-4604" data-mp="3mciu8njdfpuu">
                      {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-4605" data-mp="oi5s9sqvnuk">
                    <div className="flex items-center justify-end gap-2" data-id="element-4606" data-mp="mxbmlace6x8">
                      <button onClick={e => {
                  e.stopPropagation();
                  setDeleteConfirm(tpl.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-4607" data-mp="3ru9st0b86kd5">
                        <Trash2Icon className="h-4 w-4" data-id="element-4608" data-mp="394tr49nt0lv5" />
                      </button>
                      <ChevronRightIcon className="h-4 w-4 text-gray-300" data-id="element-4609" data-mp="392qtp4kbnqge" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4610" data-mp="1fkt76kmjekj0">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4611" data-mp="1jng0yr2rqfiu">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4612" data-mp="1jpj143xmxp1a" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4613" data-mp="1tlvrartz1kra">
              <div className="flex items-center gap-4" data-id="element-4614" data-mp="vydkhmjvespx">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4615" data-mp="vvskl334umut">
                  <Trash2Icon className="h-6 w-6 text-red-600" data-id="element-4616" data-mp="15w1kaqwc2qey" />
                </div>
                <div data-id="element-4617" data-mp="15u89r0ilnm1o">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4618" data-mp="1amhxqb05mc1b">
                    Delete Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4619" data-mp="d6chq93z6ib8">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4620" data-mp="3akp7czrbp9wl">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4621" data-mp="3am9zyju2uirt">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50" data-id="element-4622" data-mp="3pgonmxol6gct">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}